import type { FormEvent, ReactNode } from "react";
import { Group, Modal, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Button } from "./button";

interface FormDialogProps {
  open: boolean;
  title: string;
  description?: string;
  submitLabel: string;
  /** Styles the submit control for an action with consequences, like suspending service. */
  destructive?: boolean;
  pending?: boolean;
  children: ReactNode;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
}

/**
 * A form in a modal.
 *
 * Section 9 lists one route per resource and no `/new` alongside them, so
 * creating a site or inviting a member happens where the list is. Mantine's
 * Modal supplies the focus trap and Escape handling; the form itself stays
 * ours so react-hook-form + Zod keep owning validation (ADR-075).
 */
export function FormDialog({
  open,
  title,
  description,
  submitLabel,
  destructive = false,
  pending = false,
  children,
  onSubmit,
  onCancel,
}: FormDialogProps) {
  const { t } = useTranslation("common");

  return (
    <Modal
      opened={open}
      onClose={() => {
        if (!pending) {
          onCancel();
        }
      }}
      title={title}
      size="lg"
      // Instant open: a CSS-less test environment never finishes the enter
      // transition, so the dialog would stay `aria-hidden` and unfindable.
      transitionProps={{ duration: 0 }}
      closeOnClickOutside={!pending}
      closeOnEscape={!pending}
      withCloseButton={!pending}
    >
      {description ? (
        <Text size="sm" c="dimmed" mb="lg">
          {description}
        </Text>
      ) : null}

      {/* `noValidate`: the browser's own bubbles would pre-empt the Zod
          messages, and only one of the two is wired to `aria-describedby`. */}
      <form onSubmit={onSubmit} noValidate autoComplete="off">
        {/* One scroll only: Mantine Modal content already scrolls. An inner
            max-height + overflow created a nested scrollbar. */}
        <div className="[&>div]:gap-5">{children}</div>

        <Group justify="flex-end" gap="sm" mt="xl">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={pending}>
            {t("cancel")}
          </Button>
          <Button type="submit" variant={destructive ? "danger" : "primary"} loading={pending}>
            {submitLabel}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
